import { useEffect, useRef } from "react";

import { X } from "lucide-react";
import rehypeRaw from "rehype-raw";
import ReactMarkdown from "react-markdown";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import useChatStore from "@/stores/useChatStore";
import { TextareaAuto } from "@/components/ui/textarea-auto";
import useKeyboardDetection from "@/hooks/useKeyboardDetection";
import { useNavigate, useParams } from "react-router-dom";

function FooterContent({ className, isPopupContent = false, inputClassName, isMobile = false, disabled = false }) {
  const footerRef = useRef(null);
  const textareaRef = useRef(null);
  const keyboardState = useKeyboardDetection();

  const question = useChatStore((store) => store.question);
  const isAnswering = useChatStore((store) => store.isAnswering);
  const setQuestion = useChatStore((store) => store.setQuestion);
  const conversation = useChatStore((store) => store.conversation);
  const setFooterHeight = useChatStore((store) => store.setFooterHeight);
  const referenceMessage = useChatStore((store) => store.referenceMessage);
  const setReferenceMessage = useChatStore((store) => store.setReferenceMessage);
  const handleAskQuestionStream = useChatStore((store) => store.handleAskQuestionStream);
  const handleResetChatStore = useChatStore((store) => store.handleResetChatStore);
  const navigate = useNavigate();
  const { reminderId } = useParams();


  // Calculate footer height including reference
  useEffect(() => {
    if (footerRef.current) {
      const height = footerRef.current.offsetHeight;
      // Only update when changed to avoid needless rerenders
      if (useChatStore.getState().footerHeight !== height) {
        setFooterHeight(height);
      }
    }
  }, [question, setFooterHeight, referenceMessage]);

  // Auto focus to end of text when question changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [question, referenceMessage]);

  const onSubmit = () => {
    if (!question.trim()) return;

    if (question) {
      handleResetChatStore();
      setQuestion(question);
      handleAskQuestionStream(reminderId); // Use streaming instead of regular API
      setQuestion("");
      navigate("/"); // Navigate to chat page
    }
  };

  const onKeyDown = (e) => {
    if (e.keyCode === 229) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleTextareaHeightChange = (textareaHeight) => {
    const maxHeight = isMobile ? 120 : 160;
    const actualHeight = Math.min(textareaHeight, maxHeight);
    const totalHeight = actualHeight + (isMobile ? 20 : 40);
    if (useChatStore.getState().footerHeight !== totalHeight) {
      setFooterHeight(totalHeight);
    }
  };

  return (
    <div
      className={cn(
        "w-full gap-2",
        className,
        // Khi keyboard hiện trên mobile, thêm class để adjust position
        isMobile && keyboardState.isVisible && "transition-all duration-200 ease-out"
      )}
      ref={footerRef}
      style={
        isMobile && keyboardState.isVisible
          ? {
              // Đảm bảo input luôn visible khi keyboard hiện
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }
          : {}
      }
    >
      {referenceMessage && (
        <div className="mb-2 p-3 rounded-lg bg-gray-50 border border-gray-200 relative flex">
          <div className="flex-1 text-left text-sm text-gray-700 overflow-x-auto max-h-[120px] overflow-y-auto pr-2">
            <ReactMarkdown rehypePlugins={[rehypeRaw]}>{referenceMessage}</ReactMarkdown>
          </div>
          <button className="ml-2 p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" onClick={() => setReferenceMessage("")} aria-label="Xóa tham khảo">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="relative">
        <TextareaAuto
          ref={textareaRef}
          value={question}
          placeholder="Tôi có thể giúp gì cho bạn?"
          className={cn(
            "flex-1 text-xs focus-visible:border-none placeholder:text-disabled",
            "max-h-[160px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent",
            "hover:scrollbar-thumb-gray-400",
            isMobile && "max-h-[120px]",
            inputClassName
          )}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          onHeightChange={handleTextareaHeightChange}
          disabled={disabled}
        />
        <Button
          size={isPopupContent ? "sm" : "md"}
          className={cn("absolute right-0 top-1/2 -translate-y-1/2 ", isPopupContent ? "mr-2 h-7 w-7" : "mr-3 h-9 w-9")}
          disabled={!question.trim() || isAnswering || disabled}
          onClick={onSubmit}
        >
          →
        </Button>
      </div>
      <div
        className={cn("text-xs text-gray-500 text-center mt-2", {
          visible: conversation.length > 0 && !isPopupContent && !keyboardState.isVisible,
          invisible: conversation.length === 0 || isPopupContent || keyboardState.isVisible,
        })}
      >
        Trợ lý AI có thể mắc lỗi. Hãy kiểm tra các thông tin quan trọng.
      </div>
    </div>
  );
}

export default FooterContent;
